import React, { useState, useCallback, useRef } from "react";
import { useDropzone } from "react-dropzone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { apiClient } from "../../services/apiClient";

const DocumentUpload = ({ proposalId, onUploadComplete, onUploadError }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [documentTypes, setDocumentTypes] = useState([]);
  const [selectedType, setSelectedType] = useState("");
  const [queuedFiles, setQueuedFiles] = useState([]);
  const [uploadMode, setUploadMode] = useState("manual"); // Changed default to "manual"
  const fileInputRef = useRef(null);

  // Load document types on mount
  React.useEffect(() => {
    const loadDocumentTypes = async () => {
      try {
        const response = await apiClient.getDocumentTypes();
        if (response.success) {
          setDocumentTypes(response.data.documentTypes);
        }
      } catch (error) {
        console.error("Failed to load document types:", error);
      }
    };

    loadDocumentTypes();
  }, []);

  // Auto-detect document type based on filename
  const autoDetectDocumentType = (fileName) => {
    const name = fileName.toLowerCase();
    const ext = fileName.split(".").pop().toLowerCase();

    // Enhanced detection rules
    const detectionRules = [
      {
        keywords: ["rfp", "solicitation", "announcement"],
        types: ["Solicitation"],
      },
      { keywords: ["sow", "scope", "work"], types: ["Scope_of_Work"] },
      { keywords: ["resume", "cv", "biography", "bio"], types: ["Resume"] },
      {
        keywords: ["proposal", "technical", "response"],
        types: ["Technical_Proposal"],
      },
      {
        keywords: ["price", "pricing", "cost", "budget"],
        types: ["Pricing_Proposal"],
      },
      {
        keywords: ["past", "performance", "reference"],
        types: ["Past_Performance"],
      },
      {
        keywords: ["capability", "statement"],
        types: ["Capability_Statement"],
      },
    ];

    // Check filename for keywords
    for (const rule of detectionRules) {
      if (rule.keywords.some((keyword) => name.includes(keyword))) {
        const matchingType = documentTypes.find((dt) =>
          rule.types.some((type) =>
            dt.type_name.toLowerCase().includes(type.toLowerCase())
          )
        );
        if (matchingType) {
          return matchingType.type_id;
        }
      }
    }

    // Default based on file extension
    const extDefaults = {
      pdf: "Solicitation",
      docx: "Technical_Proposal",
      doc: "Technical_Proposal",
      xlsx: "Pricing_Proposal",
      xls: "Pricing_Proposal",
      pptx: "Presentation",
      ppt: "Presentation",
    };

    if (extDefaults[ext]) {
      const defaultType = documentTypes.find((dt) =>
        dt.type_name.toLowerCase().includes(extDefaults[ext].toLowerCase())
      );
      if (defaultType) {
        return defaultType.type_id;
      }
    }

    return null;
  };

  // Validate file before adding to queue
  const validateFile = (file) => {
    const allowedExtensions = [
      ".pdf",
      ".doc",
      ".docx",
      ".xls",
      ".xlsx",
      ".ppt",
      ".pptx",
    ];
    const fileExtension = "." + file.name.split(".").pop().toLowerCase();

    if (!allowedExtensions.includes(fileExtension)) {
      throw new Error(`File type not supported: ${file.name}`);
    }

    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      throw new Error(`File too large: ${file.name} (max 50MB)`);
    }

    // Check for duplicate names in queue
    if (queuedFiles.some((qf) => qf.file.name === file.name)) {
      throw new Error(`Duplicate file: ${file.name} is already in queue`);
    }

    return true;
  };

  // Add files to upload queue
  const addToQueue = useCallback(
    (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        const errors = rejectedFiles.map(
          (file) =>
            `${file.file.name}: ${file.errors.map((e) => e.message).join(", ")}`
        );
        onUploadError?.(errors.join("\n"));
      }

      const newQueuedFiles = [];
      const validationErrors = [];

      acceptedFiles.forEach((file) => {
        try {
          validateFile(file);
          const detectedType = autoDetectDocumentType(file.name);
          newQueuedFiles.push({
            id: `${file.name}-${Date.now()}-${Math.random()}`,
            file,
            detectedType,
            selectedType: uploadMode === "auto" ? detectedType : selectedType,
            status: "queued",
          });
        } catch (error) {
          validationErrors.push(error.message);
        }
      });

      if (validationErrors.length > 0) {
        onUploadError?.(validationErrors.join("\n"));
      }

      if (newQueuedFiles.length > 0) {
        setQueuedFiles((prev) => [...prev, ...newQueuedFiles]);

        // Auto-upload if in auto mode
        if (uploadMode === "auto" && proposalId) {
          setTimeout(
            () => uploadQueuedFiles([...queuedFiles, ...newQueuedFiles]),
            100
          );
        }
      }
    },
    [queuedFiles, selectedType, uploadMode, proposalId, onUploadError]
  );

  // Remove file from queue
  const removeFromQueue = (fileId) => {
    setQueuedFiles((prev) => prev.filter((qf) => qf.id !== fileId));
  };

  // Update file type in queue
  const updateFileType = (fileId, typeId) => {
    setQueuedFiles((prev) =>
      prev.map((qf) =>
        qf.id === fileId ? { ...qf, selectedType: typeId } : qf
      )
    );
  };

  // Upload queued files
  const uploadQueuedFiles = async (filesToUpload = queuedFiles) => {
    if (!proposalId) {
      onUploadError?.("Please select a proposal first");
      return;
    }

    const readyFiles = filesToUpload.filter((qf) => qf.status === "queued");
    if (readyFiles.length === 0) {
      return;
    }

    setUploading(true);
    const results = [];

    try {
      for (const queuedFile of readyFiles) {
        // Update status to uploading
        setQueuedFiles((prev) =>
          prev.map((qf) =>
            qf.id === queuedFile.id ? { ...qf, status: "uploading" } : qf
          )
        );

        // Initialize progress tracking
        setUploadProgress((prev) => ({
          ...prev,
          [queuedFile.id]: {
            fileName: queuedFile.file.name,
            progress: 0,
            status: "uploading",
          },
        }));

        try {
          const response = await apiClient.uploadDocuments(
            proposalId,
            [queuedFile.file],
            queuedFile.selectedType || null,
            (progress) => {
              setUploadProgress((prev) => ({
                ...prev,
                [queuedFile.id]: { ...prev[queuedFile.id], progress },
              }));
            }
          );

          if (response.success) {
            setUploadProgress((prev) => ({
              ...prev,
              [queuedFile.id]: {
                ...prev[queuedFile.id],
                progress: 100,
                status: "completed",
              },
            }));

            setQueuedFiles((prev) =>
              prev.map((qf) =>
                qf.id === queuedFile.id ? { ...qf, status: "completed" } : qf
              )
            );

            results.push({
              file: queuedFile.file.name,
              success: true,
              data: response.data,
            });
          } else {
            throw new Error(response.message || "Upload failed");
          }
        } catch (fileError) {
          setUploadProgress((prev) => ({
            ...prev,
            [queuedFile.id]: {
              ...prev[queuedFile.id],
              status: "error",
              error: fileError.message,
            },
          }));

          setQueuedFiles((prev) =>
            prev.map((qf) =>
              qf.id === queuedFile.id
                ? { ...qf, status: "error", error: fileError.message }
                : qf
            )
          );

          results.push({
            file: queuedFile.file.name,
            success: false,
            error: fileError.message,
          });
        }
      }

      // Clear completed/error files after delay
      setTimeout(() => {
        setQueuedFiles((prev) => prev.filter((qf) => qf.status === "queued"));
        setUploadProgress({});
      }, 3000);

      onUploadComplete?.(results);
    } catch (error) {
      onUploadError?.(error.message);
    } finally {
      setUploading(false);
    }
  };

  // Clear all queued files
  const clearQueue = () => {
    setQueuedFiles([]);
    setUploadProgress({});
  };

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragReject,
    isDragAccept,
  } = useDropzone({
    onDrop: addToQueue,
    accept: {
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.ms-powerpoint": [".ppt"],
      "application/vnd.openxmlformats-officedocument.presentationml.presentation":
        [".pptx"],
    },
    maxSize: 50 * 1024 * 1024,
    maxFiles: 10,
    disabled: uploading,
    noClick: false,
  });

  const getDropzoneClasses = () => {
    let classes =
      "border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200 cursor-pointer ";

    if (isDragAccept) {
      classes += "border-emerald-400 bg-emerald-50 text-emerald-700";
    } else if (isDragReject) {
      classes += "border-red-400 bg-red-50 text-red-700";
    } else if (isDragActive) {
      classes += "border-primary-400 bg-primary-50 text-primary-700";
    } else if (uploading) {
      classes += "border-slate-300 bg-slate-50 text-slate-500";
    } else {
      classes +=
        "border-slate-300 hover:border-primary-400 hover:bg-primary-50 text-slate-600";
    }

    return classes;
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split(".").pop()?.toLowerCase();
    const iconMap = {
      pdf: { icon: "file-pdf", color: "text-red-500" },
      doc: { icon: "file-word", color: "text-blue-500" },
      docx: { icon: "file-word", color: "text-blue-500" },
      xls: { icon: "file-excel", color: "text-emerald-500" },
      xlsx: { icon: "file-excel", color: "text-emerald-500" },
      ppt: { icon: "file-powerpoint", color: "text-orange-500" },
      pptx: { icon: "file-powerpoint", color: "text-orange-500" },
    };
    return iconMap[ext] || { icon: "file-alt", color: "text-slate-500" };
  };

  const formatFileSize = (bytes) => {
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + " " + sizes[i];
  };

  return (
    <div className="space-y-6">
      {/* Upload Mode Selection */}
      <div className="flex items-center space-x-6">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Upload Mode
          </label>
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                value="manual"
                checked={uploadMode === "manual"}
                onChange={(e) => setUploadMode(e.target.value)}
                className="mr-2"
              />
              <span className="text-sm">Manual review</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="auto"
                checked={uploadMode === "auto"}
                onChange={(e) => setUploadMode(e.target.value)}
                className="mr-2"
              />
              <span className="text-sm">Auto-upload</span>
            </label>
          </div>
        </div>

        {/* Default Document Type for Manual Mode */}
        {uploadMode === "manual" && documentTypes.length > 0 && (
          <div className="flex-1">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Default Document Type
            </label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              disabled={uploading}
            >
              <option value="">Auto-detect type</option>
              {documentTypes.map((type) => (
                <option key={type.type_id} value={type.type_id}>
                  {type.type_name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Upload Area */}
      <div {...getRootProps()} className={getDropzoneClasses()}>
        <input {...getInputProps()} />

        <div className="space-y-4">
          <div className="mx-auto w-16 h-16 flex items-center justify-center">
            {uploading ? (
              <FontAwesomeIcon
                icon="spinner"
                className="text-3xl text-primary-500 animate-spin"
              />
            ) : (
              <FontAwesomeIcon
                icon="upload"
                className={`text-3xl ${
                  isDragAccept
                    ? "text-emerald-500"
                    : isDragReject
                    ? "text-red-500"
                    : "text-slate-400"
                }`}
              />
            )}
          </div>

          <div>
            {isDragActive ? (
              isDragReject ? (
                <p className="text-lg font-medium text-red-700">
                  Some files are not supported
                </p>
              ) : (
                <p className="text-lg font-medium text-emerald-700">
                  Drop files here to upload
                </p>
              )
            ) : uploading ? (
              <p className="text-lg font-medium text-slate-700">
                Uploading files...
              </p>
            ) : !proposalId ? (
              <div>
                <p className="text-lg font-medium text-slate-500">
                  Select a proposal to upload documents
                </p>
              </div>
            ) : (
              <div>
                <p className="text-lg font-medium text-slate-700">
                  Drag & drop files here, or click to browse
                </p>
                <p className="text-sm text-slate-500 mt-2">
                  Supports PDF, Word, Excel, PowerPoint (max 50MB each, up to 10
                  files)
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  {uploadMode === "auto"
                    ? "Files will upload automatically"
                    : "Review files before uploading"}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* File Queue (Manual Mode) */}
      {uploadMode === "manual" && queuedFiles.length > 0 && (
        <div className="bg-slate-50 rounded-xl p-4">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-medium text-slate-700">
              Upload Queue (
              {queuedFiles.filter((qf) => qf.status === "queued").length} files)
            </h4>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => uploadQueuedFiles()}
                disabled={
                  uploading ||
                  queuedFiles.filter((qf) => qf.status === "queued").length ===
                    0
                }
                className="px-3 py-1 bg-primary-500 text-white rounded-md text-sm hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Upload All
              </button>
              <button
                onClick={clearQueue}
                disabled={uploading}
                className="px-3 py-1 bg-slate-500 text-white rounded-md text-sm hover:bg-slate-600 disabled:opacity-50"
              >
                Clear Queue
              </button>
            </div>
          </div>

          <div className="space-y-2 max-h-64 overflow-y-auto">
            {queuedFiles.map((queuedFile) => {
              const fileIcon = getFileIcon(queuedFile.file.name);
              const progress = uploadProgress[queuedFile.id];

              return (
                <div
                  key={queuedFile.id}
                  className="bg-white rounded-lg p-3 border border-slate-200"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <FontAwesomeIcon
                        icon={fileIcon.icon}
                        className={`${fileIcon.color} flex-shrink-0`}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {queuedFile.file.name}
                        </p>
                        <p className="text-xs text-slate-500">
                          {formatFileSize(queuedFile.file.size)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      {queuedFile.status === "queued" && (
                        <>
                          <select
                            value={queuedFile.selectedType || ""}
                            onChange={(e) =>
                              updateFileType(queuedFile.id, e.target.value)
                            }
                            className="text-xs px-2 py-1 border border-slate-300 rounded"
                            disabled={uploading}
                          >
                            <option value="">Auto-detect</option>
                            {documentTypes.map((type) => (
                              <option key={type.type_id} value={type.type_id}>
                                {type.type_name}
                              </option>
                            ))}
                          </select>
                          <button
                            onClick={() => removeFromQueue(queuedFile.id)}
                            className="text-slate-400 hover:text-red-500 p-1"
                            disabled={uploading}
                          >
                            <FontAwesomeIcon icon="times" />
                          </button>
                        </>
                      )}

                      {queuedFile.status === "uploading" && (
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-slate-500">
                            {progress?.progress || 0}%
                          </span>
                          <FontAwesomeIcon
                            icon="spinner"
                            className="animate-spin text-primary-500"
                          />
                        </div>
                      )}

                      {queuedFile.status === "completed" && (
                        <FontAwesomeIcon
                          icon="check"
                          className="text-emerald-500"
                        />
                      )}

                      {queuedFile.status === "error" && (
                        <FontAwesomeIcon
                          icon="times"
                          className="text-red-500"
                        />
                      )}
                    </div>
                  </div>

                  {/* Progress Bar */}
                  {queuedFile.status === "uploading" && progress && (
                    <div className="mt-2">
                      <div className="w-full bg-slate-200 rounded-full h-1">
                        <div
                          className="bg-primary-500 h-1 rounded-full transition-all duration-300"
                          style={{ width: `${progress.progress}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Error Message */}
                  {queuedFile.status === "error" && queuedFile.error && (
                    <p className="text-xs text-red-600 mt-2">
                      {queuedFile.error}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Upload Progress (Auto Mode) */}
      {uploadMode === "auto" && Object.keys(uploadProgress).length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-slate-700">
            Upload Progress:
          </h4>
          {Object.entries(uploadProgress).map(([fileId, progress]) => (
            <div key={fileId} className="bg-slate-50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <FontAwesomeIcon
                    icon={getFileIcon(progress.fileName).icon}
                    className={getFileIcon(progress.fileName).color}
                  />
                  <span className="text-sm font-medium text-slate-700 truncate">
                    {progress.fileName}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  {progress.status === "uploading" && (
                    <span className="text-xs text-slate-500">
                      {progress.progress}%
                    </span>
                  )}
                  {progress.status === "completed" && (
                    <FontAwesomeIcon
                      icon="check"
                      className="text-emerald-500"
                    />
                  )}
                  {progress.status === "error" && (
                    <FontAwesomeIcon icon="times" className="text-red-500" />
                  )}
                </div>
              </div>

              {progress.status === "uploading" && (
                <div className="w-full bg-slate-200 rounded-full h-2">
                  <div
                    className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progress.progress}%` }}
                  />
                </div>
              )}

              {progress.status === "error" && (
                <p className="text-xs text-red-600 mt-1">{progress.error}</p>
              )}

              {progress.status === "completed" && (
                <p className="text-xs text-emerald-600 mt-1">
                  Upload completed successfully
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DocumentUpload;
